/**
 * Mapping des pays pour normalisation et affichage.
 *
 * Périmètre pragmatique : les ~25 pays déjà proposés dans les formulaires créateur
 * (France + francophonie + quelques marchés européens/internationaux courants),
 * pas une base ISO-3166 complète. Une valeur non reconnue reste visible (nettoyée)
 * plutôt que silencieusement perdue — la diversité géographique est une donnée
 * métier qu'on ne veut pas écraser.
 */

export type NormalizedCountry = string;

export const CANONICAL_COUNTRIES: string[] = [
  'France', 'Belgique', 'Suisse', 'Canada', 'Luxembourg',
  'Maroc', 'Algérie', 'Tunisie', 'Sénégal', "Côte d'Ivoire",
  'Cameroun', 'Madagascar', 'Réunion', 'Martinique', 'Guadeloupe',
  'Royaume-Uni', 'Espagne', 'Italie', 'Allemagne', 'Autriche', 'Pays-Bas',
  'Portugal', 'États-Unis', 'Brésil', 'Mexique', 'Autre',
];

interface CountryMapping {
  normalized: string;
  variants: string[];
}

const COUNTRY_VARIATIONS: CountryMapping[] = [
  { normalized: 'France', variants: ['france', 'fr'] },
  { normalized: 'Belgique', variants: ['belgium', 'belgique', 'be'] },
  { normalized: 'Suisse', variants: ['switzerland', 'suisse', 'ch'] },
  { normalized: 'Canada', variants: ['canada', 'ca'] },
  { normalized: 'Luxembourg', variants: ['luxembourg', 'lu'] },
  { normalized: 'Maroc', variants: ['morocco', 'maroc', 'ma'] },
  { normalized: 'Algérie', variants: ['algeria', 'algerie', 'dz'] },
  { normalized: 'Tunisie', variants: ['tunisia', 'tunisie', 'tn'] },
  { normalized: 'Sénégal', variants: ['senegal', 'sn'] },
  { normalized: "Côte d'Ivoire", variants: ['ivory coast', 'cote d ivoire', 'cote divoire', 'ci'] },
  { normalized: 'Cameroun', variants: ['cameroon', 'cameroun', 'cm'] },
  { normalized: 'Madagascar', variants: ['madagascar', 'mg'] },
  { normalized: 'Réunion', variants: ['reunion', 're'] },
  { normalized: 'Martinique', variants: ['martinique', 'mq'] },
  { normalized: 'Guadeloupe', variants: ['guadeloupe', 'gp'] },
  { normalized: 'Royaume-Uni', variants: ['united kingdom', 'uk', 'gb', 'england', 'royaume uni'] },
  { normalized: 'Espagne', variants: ['spain', 'espagne', 'es'] },
  { normalized: 'Italie', variants: ['italy', 'italie', 'it'] },
  { normalized: 'Allemagne', variants: ['germany', 'deutschland', 'allemagne', 'de'] },
  { normalized: 'Autriche', variants: ['austria', 'osterreich', 'at'] },
  { normalized: 'Pays-Bas', variants: ['netherlands', 'holland', 'pays bas', 'nl'] },
  { normalized: 'Portugal', variants: ['portugal', 'pt'] },
  { normalized: 'États-Unis', variants: ['united states', 'usa', 'us', 'etats unis'] },
  { normalized: 'Brésil', variants: ['brazil', 'bresil', 'br'] },
  { normalized: 'Mexique', variants: ['mexico', 'mx'] },
];

function normalizeSearchTerm(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/['']/g, '')
    .replace(/[-_/]/g, ' ')
    .replace(/\s+/g, ' ');
}

const variantsToNormalizedMap = new Map<string, string>();
COUNTRY_VARIATIONS.forEach(({ normalized, variants }) => {
  variantsToNormalizedMap.set(normalizeSearchTerm(normalized), normalized);
  variants.forEach(variant => variantsToNormalizedMap.set(normalizeSearchTerm(variant), normalized));
});

/**
 * Normalise un pays : casse/accents/codes ISO tolérés. Un pays non reconnu est
 * retourné nettoyé (première lettre capitalisée) plutôt que perdu ou regroupé
 * dans un bucket générique.
 */
export function normalizeCountry(input: string): NormalizedCountry {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return '';

  const match = variantsToNormalizedMap.get(normalizeSearchTerm(trimmed));
  if (match) return match;

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Traite une liste de pays bruts et retourne les valeurs normalisées uniques.
 */
export function processInputCountries(inputs: string[]): NormalizedCountry[] {
  const set = new Set<string>();
  inputs.forEach(input => {
    if (!input || typeof input !== 'string') return;
    set.add(normalizeCountry(input));
  });
  return Array.from(set).sort();
}
